import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { verticalAPI, organizationAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Layers,
  Loader2,
  Search,
  Filter,
  Globe,
} from 'lucide-react';

export default function VerticalsPage() {
  const [verticals, setVerticals] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOrg, setFilterOrg] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVertical, setEditingVertical] = useState(null);
  const [formData, setFormData] = useState({ name: '', organization_id: '', is_global: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [vertRes, orgRes] = await Promise.all([
        verticalAPI.getAll(),
        organizationAPI.getAll(),
      ]);
      setVerticals(vertRes.data);
      setOrganizations(orgRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vert = null) => {
    if (vert) {
      setEditingVertical(vert);
      setFormData({ 
        name: vert.name, 
        organization_id: vert.organization_id || '',
        is_global: vert.is_global || !vert.organization_id
      });
    } else {
      setEditingVertical(null);
      setFormData({ name: '', organization_id: '', is_global: false });
    }
    setDialogOpen(true);
  };

  const handleGlobalChange = (checked) => {
    setFormData({ 
      ...formData, 
      is_global: checked,
      organization_id: checked ? '' : formData.organization_id
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        name: formData.name,
        organization_id: formData.is_global ? null : formData.organization_id,
        is_global: formData.is_global
      };

      if (editingVertical) {
        await verticalAPI.update(editingVertical.id, submitData);
        toast.success('Vertical updated successfully');
      } else {
        await verticalAPI.create(submitData);
        toast.success('Vertical created successfully');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vert) => {
    try {
      await verticalAPI.delete(vert.id);
      toast.success('Vertical deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getOrgName = (orgId) => {
    if (!orgId) return null;
    return organizations.find(o => o.id === orgId)?.name || '-';
  };

  const filteredVerticals = verticals.filter((vert) => {
    const matchesSearch = vert.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesOrg = filterOrg === 'all' || 
                       filterOrg === 'global' ? (vert.is_global || !vert.organization_id) :
                       vert.organization_id === filterOrg;
    return matchesSearch && matchesOrg;
  });

  return (
    <DashboardLayout>
      <div data-testid="verticals-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Verticals</h1>
            <p className="text-muted-foreground mt-1">
              Manage departments and teams across organizations
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-vertical-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Vertical
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingVertical ? 'Edit Vertical' : 'Add Vertical'}
                </DialogTitle>
                <DialogDescription>
                  {editingVertical ? 'Update vertical details' : 'Create a new vertical/department'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Vertical Name</Label>
                    <Input
                      id="name"
                      placeholder="Engineering"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="vertical-name-input"
                    />
                  </div>

                  <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
                    <Checkbox
                      id="is_global"
                      checked={formData.is_global}
                      onCheckedChange={handleGlobalChange}
                      data-testid="vertical-global-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="is_global" className="flex items-center gap-2 cursor-pointer">
                        <Globe className="h-4 w-4 text-blue-500" />
                        Common Vertical
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Available across all organizations
                      </p>
                    </div>
                  </div>

                  {!formData.is_global && (
                    <div className="space-y-2">
                      <Label>Organization</Label>
                      <Select
                        value={formData.organization_id}
                        onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                      >
                        <SelectTrigger data-testid="vertical-org-select">
                          <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={submitting || (!formData.is_global && !formData.organization_id)} 
                    data-testid="vertical-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 spinner" />
                        Saving...
                      </>
                    ) : editingVertical ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search verticals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="vertical-search-input"
            />
          </div>
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-[180px]" data-testid="filter-org-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Verticals</SelectItem>
              <SelectItem value="global">
                <span className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Common Only
                </span>
              </SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
              </div>
            ) : filteredVerticals.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Layers className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery || filterOrg !== 'all' ? 'No verticals found' : 'No verticals yet'}
                </p>
                {!searchQuery && filterOrg === 'all' && (
                  <Button variant="link" onClick={() => handleOpenDialog()} className="mt-2">
                    Create your first vertical
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vertical Name</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVerticals.map((vert) => (
                    <TableRow key={vert.id} className="table-row-hover" data-testid={`vertical-row-${vert.id}`}>
                      <TableCell className="font-medium">{vert.name}</TableCell>
                      <TableCell>
                        {vert.is_global || !vert.organization_id ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                            <Globe className="h-3 w-3 mr-1" />
                            Common
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">{getOrgName(vert.organization_id)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {formatDate(vert.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(vert)}
                            data-testid={`edit-vertical-${vert.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-vertical-${vert.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Vertical</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{vert.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(vert)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
